/* eslint react-hooks/exhaustive-deps: "warn" */

import {
  forwardRef,
  ReactNode,
  RefObject,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
  type JSX,
} from 'react';

import cn from 'classnames';

import { useDraggableScroll } from 'hooks/useDraggableScroll';
import { useEventListener } from 'hooks/useEventListener';

import { Icon } from 'ui/atoms/Icon/Icon';

import styles from './Slider.module.scss';

export interface SliderRef {
  sliderRef: RefObject<HTMLDivElement | null>;
  onLeftTransition: () => void;
  onRightTransition: () => void;
  hasLeftTransition: boolean;
  hasRightTransition: boolean;
}

interface SliderProps {
  children?: JSX.Element[] | ReactNode | any;
  className?: string;
  heightArrow?: 'top' | 'middle';
  arrowType?: 'primary' | 'secondary';
  hasArrows?: boolean;
  onSlideLeft?: () => void;
  onSlideRight?: () => void;
  onRecalcTransitions?: () => void;
  onTransitionsChange?: (hasLeft: boolean, hasRight: boolean) => void;
}

export const Slider = forwardRef<SliderRef, SliderProps>(
  (
    {
      onRecalcTransitions,
      onTransitionsChange,
      children,
      className,
      heightArrow = 'top',
      arrowType = 'primary',
      hasArrows = true,
    },
    ref,
  ) => {
    const [mounted, setMounted] = useState(false);
    const [showArrows, setShowArrows] = useState(false);

    const sliderRef = useRef<null | HTMLDivElement>(null);
    const scrollThrottle = useRef<NodeJS.Timeout>(undefined);
    const onTransitionsChangeRef = useRef(onTransitionsChange);

    const { onLeftTransition, onRightTransition, hasLeftTransition, hasRightTransition, recalcTransitions } =
      useDraggableScroll(sliderRef, [children, mounted], { onRecalcTransitions });

    useEffect(() => {
      onTransitionsChangeRef.current = onTransitionsChange;
    });

    useEffect(() => {
      setMounted(true);
    }, []);

    useEffect(() => {
      if (hasArrows === false) {
        setShowArrows(hasArrows);
        return;
      }

      if (sliderRef.current) {
        setShowArrows(sliderRef.current.scrollWidth > sliderRef.current.offsetWidth);
        recalcTransitions();
      }
    }, [mounted, children, recalcTransitions, hasArrows]);

    useImperativeHandle(ref, () => ({
      sliderRef,
      onLeftTransition,
      onRightTransition,
      hasLeftTransition,
      hasRightTransition,
    }));

    useEffect(() => {
      const el = sliderRef.current;
      if (!el) {
        return;
      }

      const notify = () => {
        onTransitionsChangeRef.current?.(el.scrollLeft > 1, el.scrollLeft < el.scrollWidth - el.clientWidth - 1);
      };

      notify();
      el.addEventListener('scroll', notify, { passive: true });
      return () => el.removeEventListener('scroll', notify);
    }, [mounted]);

    useEventListener(
      'scroll',
      useCallback(() => {
        if (scrollThrottle.current) {
          clearTimeout(scrollThrottle.current);
        }

        scrollThrottle.current = setTimeout(() => {
          if (sliderRef.current) {
            recalcTransitions();
          }
        }, 100);
      }, [recalcTransitions]),
      sliderRef.current,
    );

    return (
      <div className={cn(styles['slider-wrapper'], className)}>
        {showArrows && (
          <button
            tabIndex={0}
            onClick={onLeftTransition}
            disabled={!hasLeftTransition}
            aria-label="Arrow left"
            className={cn(
              styles['slider-arrow'],
              !hasLeftTransition && styles['hide'],
              styles[`slider-arrow-${heightArrow}`],
              styles[`slider-arrow-type-${arrowType}`],
            )}
          >
            <Icon type="arrow-8" />
          </button>
        )}

        <div ref={sliderRef} className={styles['slider']}>
          {children}
        </div>

        {showArrows && (
          <button
            tabIndex={0}
            onClick={onRightTransition}
            disabled={!hasRightTransition}
            aria-label="Arrow right"
            className={cn(
              styles['slider-arrow'],
              !hasRightTransition && styles['hide'],
              styles[`slider-arrow-${heightArrow}`],
              styles[`slider-arrow-type-${arrowType}`],
            )}
          >
            <Icon type="arrow-9" />
          </button>
        )}
      </div>
    );
  },
);
